import {
    computed,
    Directive,
    effect,
    inject,
    Injector,
    input,
    runInInjectionContext,
    TemplateRef,
    ViewContainerRef
} from "@angular/core";
import {
    canEdit,
    canView,
    providePermissions,
    RolePermissionParameters,
    updatePermissions
} from "~app/role-permissions";

export interface PermittedDirectiveContext {
    $implicit: boolean;
    canView: boolean;
    canEdit: boolean;
}

@Directive({
    selector: "[raskPermitted]"
})
export class PermittedDirective {
    raskPermitted = input<RolePermissionParameters[]>();
    raskPermittedMerge = input(false);
    raskPermittedHide = input(true);

    private readonly viewContainer = inject(ViewContainerRef);
    private readonly templateRef = inject(TemplateRef<PermittedDirectiveContext>);
    private readonly injector = inject(Injector);

    private readonly effectiveInjector = computed(() => {
        const permissions = this.raskPermitted();
        if (!permissions?.length) { return this.injector; }

        const providerFn = this.raskPermittedMerge() ? updatePermissions : providePermissions;
        return Injector.create({
            parent: this.injector,
            providers: [providerFn(...permissions)]
        });
    });

    private readonly context = computed((): PermittedDirectiveContext => {
        const injector = this.effectiveInjector();
        return runInInjectionContext(injector, () => {
            const view = canView()();
            const edit = canEdit()();
            return {
                $implicit: view,
                canView: view,
                canEdit: edit
            };
        });
    });

    private readonly isAllowed = computed(() => this.context().canView);

    constructor() {
        effect(() => {
            const allowed = this.isAllowed();
            const injector = this.effectiveInjector();
            const ctx = this.context();

            this.viewContainer.clear();

            if (allowed || !this.raskPermittedHide()) {
                const options = injector !== this.injector ? { injector } : undefined;
                this.viewContainer.createEmbeddedView(this.templateRef, ctx, options);
            }
        });
    }

    static ngTemplateContextGuard(
        _dir: PermittedDirective,
        _ctx: unknown
    ): _ctx is PermittedDirectiveContext {
        return true;
    }
}
